// src/client/game/input/VirtualDPad.ts

import Phaser from "phaser";
import { Direction } from "../core/model";

type ArrowKey = "up" | "down" | "left" | "right";

type ArrowButton = {
  key: ArrowKey;
  label: Phaser.GameObjects.Text;
};

export class VirtualDPad {
  public isMobileLayout = false;

  private fireButton?: Phaser.GameObjects.Arc;
  private fireGraphics?: Phaser.GameObjects.Graphics;

  private dpadGraphics?: Phaser.GameObjects.Graphics;
  private dpadHitCircle?: Phaser.GameObjects.Arc;

  private arrows: ArrowButton[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly canHandleInput: () => boolean,
    private readonly canFire: () => boolean,
    private readonly onDirection: (dir: Direction) => void,
    private readonly onFire: () => void
  ) {}

  create(): void {
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;

    // Simple aspect-ratio check; DO NOT early-return just because OS is desktop.
    const isMobileLayout = height > width || width < 640;
    this.isMobileLayout = isMobileLayout;
    if (!isMobileLayout) return;

    // Bottom 40% reserved for controls
    const controlsTop = height * 0.6;
    const controlsHeight = height - controlsTop;



    const leftWidth = width / 3;
    const rightWidth = width - leftWidth;

    const controlsCenterY = controlsTop + controlsHeight / 2;

    // ==========================
    // SIMPLE FIRE BUTTON (8-bit style)
    // ==========================
    const fireCenterX = leftWidth / 2;
    const fireRadius = Math.min(leftWidth, controlsHeight) * 0.45;

    const fireNormalAlpha = 1.0;
    const firePressedAlpha = 0.6;

    const fg = this.scene.add.graphics();
    fg.setDepth(498).setScrollFactor(0);

    // Flat red disk with white outline
    fg.fillStyle(0xff3333, fireNormalAlpha);
    fg.fillCircle(fireCenterX, controlsCenterY, fireRadius);

    fg.lineStyle(2, 0xffffff, 1);
    fg.strokeCircle(fireCenterX, controlsCenterY, fireRadius);

    this.fireGraphics = fg;

    // Hit circle
    this.fireButton = this.scene.add.circle(
      fireCenterX,
      controlsCenterY,
      fireRadius * 0.9,
      0xffffff,
      0.001
    );
    this.fireButton
      .setDepth(499)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    const setFireAlpha = (alpha: number) => {
      this.fireGraphics?.setAlpha(alpha);
    };

    this.fireButton.on("pointerdown", () => {
      setFireAlpha(firePressedAlpha);
      if (this.canFire()) {
        this.onFire();
      }
    });

    const resetFire = () => setFireAlpha(fireNormalAlpha);
    this.fireButton.on("pointerup", resetFire);
    this.fireButton.on("pointerupoutside", resetFire);
    this.fireButton.on("pointerout", resetFire);

    // ==========================
    // SIMPLE CIRCULAR DPAD
    // ==========================

    const dpadCenterX = leftWidth + rightWidth / 2;
    const dpadRadius = Math.min(rightWidth, controlsHeight) * 0.45;
    const dpadCenterY = controlsCenterY;

    const g = this.scene.add.graphics();
    g.setDepth(498).setScrollFactor(0);

    // Flat dark-gray disk
    g.fillStyle(0x222222, 0.9);
    g.fillCircle(dpadCenterX, dpadCenterY, dpadRadius);

    // White outline
    g.lineStyle(2, 0xffffff, 1);
    g.strokeCircle(dpadCenterX, dpadCenterY, dpadRadius);

    // Diagonal lines, thin + simple
    g.lineStyle(1, 0x888888, 0.9);
    g.beginPath();

    const r = dpadRadius;

    // 45° to 225°
    const p1x = dpadCenterX + Math.cos((45 * Math.PI) / 180) * r;
    const p1y = dpadCenterY + Math.sin((45 * Math.PI) / 180) * r;
    const p2x = dpadCenterX + Math.cos((225 * Math.PI) / 180) * r;
    const p2y = dpadCenterY + Math.sin((225 * Math.PI) / 180) * r;
    g.moveTo(p1x, p1y);
    g.lineTo(p2x, p2y);

    // 135° to 315°
    const p3x = dpadCenterX + Math.cos((135 * Math.PI) / 180) * r;
    const p3y = dpadCenterY + Math.sin((135 * Math.PI) / 180) * r;
    const p4x = dpadCenterX + Math.cos((315 * Math.PI) / 180) * r;
    const p4y = dpadCenterY + Math.sin((315 * Math.PI) / 180) * r;
    g.moveTo(p3x, p3y);
    g.lineTo(p4x, p4y);

    g.strokePath();

    this.dpadGraphics = g;

    // Arrow labels – flat white “text triangles” to match 8-bit vibe
    const arrowFontSize = Math.round(dpadRadius * 0.45);
    const arrowOffset = dpadRadius * 0.6;

    const arrowNormalAlpha = 1.0;
    const arrowPressedAlpha = 0.4;

    const makeArrowLabel = (
      key: ArrowKey,
      text: string,
      x: number,
      y: number
    ) => {
      const label = this.scene.add
        .text(x, y, text, {
          fontFamily: "monospace",
          fontSize: `${arrowFontSize}px`,
          color: "#ffffff",
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(500)
        .setAlpha(arrowNormalAlpha);

      this.arrows.push({ key, label });
    };

    makeArrowLabel("up", "▲", dpadCenterX, dpadCenterY - arrowOffset);
    makeArrowLabel("down", "▼", dpadCenterX, dpadCenterY + arrowOffset);
    makeArrowLabel("left", "◀", dpadCenterX - arrowOffset, dpadCenterY);
    makeArrowLabel("right", "▶", dpadCenterX + arrowOffset, dpadCenterY);

    const setArrowHighlight = (active: ArrowKey | null) => {
      for (const arrow of this.arrows) {
        arrow.label.setAlpha(
          arrow.key === active ? arrowPressedAlpha : arrowNormalAlpha
        );
      }
    };

    // Hit circle for quadrant detection
    const hitCircle = this.scene.add.circle(
      dpadCenterX,
      dpadCenterY,
      dpadRadius,
      0x000000,
      0.001
    );
    hitCircle
      .setDepth(499)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    const handleDpadPress = (pointer: Phaser.Input.Pointer) => {
      const dx = pointer.x - dpadCenterX;
      const dy = pointer.y - dpadCenterY;

      // Outside circle => ignore
      if (dx * dx + dy * dy > dpadRadius * dpadRadius) return;
      if (!this.canHandleInput()) return;

      let key: ArrowKey;
      let dir: Direction;

      // Quadrants defined by diagonals y = x and y = -x
      if (dy < dx && dy < -dx) {
        key = "up";
        dir = { dx: 0, dy: -1 };
      } else if (dy > dx && dy > -dx) {
        key = "down";
        dir = { dx: 0, dy: 1 };
      } else if (dy > dx && dy < -dx) {
        key = "left";
        dir = { dx: -1, dy: 0 };
      } else {
        key = "right";
        dir = { dx: 1, dy: 0 };
      }

      setArrowHighlight(key);
      this.onDirection(dir);
    };

    hitCircle.on("pointerdown", handleDpadPress);

    const resetDpad = () => setArrowHighlight(null);
    hitCircle.on("pointerup", resetDpad);
    hitCircle.on("pointerupoutside", resetDpad);
    hitCircle.on("pointerout", resetDpad);

    this.dpadHitCircle = hitCircle;
  }

  destroy(): void {
    if (!this.isMobileLayout) return;

    this.fireButton?.destroy();
    this.fireButton = undefined;

    this.fireGraphics?.destroy();
    this.fireGraphics = undefined;

    this.dpadHitCircle?.destroy();
    this.dpadHitCircle = undefined;

    this.dpadGraphics?.destroy();
    this.dpadGraphics = undefined;


    for (const arrow of this.arrows) {
      arrow.label.destroy();
    }
    this.arrows = [];
  }
}
